import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  googleAuth: (data) => api.post('/auth/google', data),
  getMe: () => api.get('/auth/me'),
};

// Client API
export const clientAPI = {
  getProfile: () => api.get('/clients/profile'),
  updateProfile: (data) => api.put('/clients/profile', data),
  getMyCases: () => api.get('/clients/my-cases'),
};

// Attorney API
export const attorneyAPI = {
  getAll: (params) => api.get('/attorneys', { params }),
  suggest: (q) => api.get('/attorneys/suggest', { params: { q } }),
  getById: (id) => api.get(`/attorneys/${id}`),
  getProfile: () => api.get('/attorneys/profile'),
  updateProfile: (data) => api.put('/attorneys/profile', data),
  subscribe: (data) => api.post('/attorneys/subscribe', data),
  getSubscriptionStatus: () => api.get('/attorneys/subscription'),
  getViewStats: () => api.get('/attorneys/profile/views'),
  uploadProfilePicture: (file) => {
    const form = new FormData();
    form.append('profilePicture', file);
    return api.post('/attorneys/profile-picture', form, {
      headers: { 'Content-Type': undefined },
    });
  },
};

export const surveyAPI = {
  submit: (question, answer) => api.post('/survey', { question, answer }),
  submitNoReason: (reason, region) => api.post('/survey/no-reason', { reason, region }),
};

export const publicRequestAPI = {
  submit: (data) => api.post('/public-requests', data),
  getAll: (params) => api.get('/public-requests', { params }),
};

export const accountDeletionAPI = {
  submit: (data) => api.post('/account-deletion', data),
};


// Advice API
export const adviceAPI = {
  createRequest: (data) => api.post('/advice/request', data),
  processPayment: (data) => api.post('/advice/payment', data),
  getMyRequests: () => api.get('/advice/my-requests'),
  getUnreadAnswerCount: () => api.get('/advice/unread-answer-count'),
  markAnswerRead: (id) => api.put(`/advice/my-requests/${id}/read-answer`),
};


export const messageAPI = {
  send: (data) => api.post('/messages', data),
  getInbox: () => api.get('/messages/inbox'),
  getSent: () => api.get('/messages/sent'),
  getClientUnreadCount: () => api.get('/messages/client-unread-count'),
  getAttorneyUnreadCount: () => api.get('/messages/unread-count'),
  markRead: (id) => api.put(`/messages/${id}/read`),
  clientRead: (id) => api.put(`/messages/${id}/client-read`),
  getReplies: (id) => api.get(`/messages/${id}/replies`),
  sendReply: (id, content) => api.post(`/messages/${id}/reply`, { content }),
};

export const savedAttorneyAPI = {
  list: () => api.get('/saved-attorneys'),
  listIds: () => api.get('/saved-attorneys/ids'),
  getMyCount: () => api.get('/saved-attorneys/my-count'),
  add: (attorneyId) => api.post(`/saved-attorneys/${attorneyId}`),
  remove: (attorneyId) => api.delete(`/saved-attorneys/${attorneyId}`),
};

export const analyticsAPI = {
  searchClick: (practiceArea, location, search) => api.post('/analytics/search-click', { practiceArea, location, search }).catch(() => {}),
  callClick: (attorneyId) => api.post('/analytics/call-click', { attorneyId }).catch(() => {}),
};

export const ratingAPI = {
  get: (attorneyId) => api.get(`/ratings/${attorneyId}`),
  bulk: (ids) => api.post('/ratings/bulk', { ids }),
  submit: (attorneyId, stars) => api.post(`/ratings/${attorneyId}`, { stars }),
};

export const paymentAPI = {
  initiatePayme:        (description) => api.post('/payment/payme-initiate', { description }),
  initiateClick:        (description) => api.post('/payment/click-initiate',  { description }),
  initiatePaymeSubscription: (planType) => api.post('/payment/payme-subscription', { planType }),
  initiateClickSubscription: (planType) => api.post('/payment/click-subscription', { planType }),
  getStatus:            (id)          => api.get(`/payment/status/${id}`),
};

// Attorney article authoring API
export const articleAPI = {
  listMine:      ()         => api.get('/articles/mine'),
  getOne:        (id)       => api.get(`/articles/${id}`),
  create:        (data)     => api.post('/articles', data),
  update:        (id, data) => api.put(`/articles/${id}`, data),
  submit:        (id)       => api.post(`/articles/${id}/submit`),
  saveEdit:      (id, data) => api.put(`/articles/${id}/save-edit`, data),
  submitEdit:    (id, data) => api.post(`/articles/${id}/submit-edit`, data),
  remove:        (id)       => api.delete(`/articles/${id}`),
  listPublished: ()         => api.get('/articles/published'),
};

// Attorney ATR-100 personalized-rating submission API (VIP-only)
export const attorneyRatingAPI = {
  getMine: () => api.get('/attorney-ratings/mine'),
  updateMine: (data) => api.put('/attorney-ratings/mine', data),
  submitMine: () => api.post('/attorney-ratings/mine/submit'),
  uploadFile: (file, category) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/attorney-ratings/files?category=${encodeURIComponent(category)}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Maps backend English error strings to translation keys
const BACKEND_ERROR_MAP = {
  'User already exists': 'userAlreadyExists',
  'Invalid credentials': 'invalidCredentials',
  'Server error': 'serverError',
  'Something went wrong!': 'serverError',
  'Attorney not found': 'attorneyNotFound',
  'Profile not found': 'profileNotFound',
  'Attorney profile not found': 'profileNotFound',
  'Client profile not found': 'profileNotFound',
  'User not found': 'profileNotFound',
  'Access denied': 'accessDenied',
  'No token, authorization denied': 'accessDenied',
  'Token is not valid': 'accessDenied',
  'Invalid score': 'invalidScore',
  'Already rated': 'alreadyRated',
  'Please include a valid email': 'emailRequired',
  'Email or phone is required': 'emailRequired',
  'Password is required': 'passwordRequired',
  'Password must be at least 6 characters': 'passwordTooShort',
  'Google authentication failed': 'googleLoginFailed',
  'Full name is required': 'fullNameRequiredError',
  'Phone number is required': 'phoneRequiredError',
};

// ─── CRM API ──────────────────────────────────────────────────────────────────
const crmApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});
crmApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('crm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
crmApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('crm_token');
      window.location.href = '/crm';
    }
    return Promise.reject(error);
  }
);

export const crmAPI = {
  login: (data) => crmApi.post('/crm/auth/login', data),
  getRequests: (status) => crmApi.get('/crm/requests', { params: status ? { status } : {} }),
  takeRequest: (id) => crmApi.post(`/crm/requests/${id}/take`),
  answerRequest: (id, answer) => crmApi.post(`/crm/requests/${id}/answer`, { answer }),
  getStaff: () => crmApi.get('/crm/staff'),
  createStaff: (data) => crmApi.post('/crm/staff', data),
  getSurveyStats: () => crmApi.get('/crm/stats/survey'),
  getSurveyNoReasons: () => crmApi.get('/crm/stats/survey-no-reasons'),
  getNoRegionBreakdown: () => crmApi.get('/crm/stats/no-region-breakdown'),
  getClickStats: () => crmApi.get('/crm/stats/clicks'),
  getCallStats: () => crmApi.get('/crm/stats/calls'),
  getStaffStats: () => crmApi.get('/crm/stats/staff'),
  getActiveAttorneyStats: () => crmApi.get('/crm/stats/active-attorneys'),
  getFinancials: () => crmApi.get('/crm/financials'),
  getArticleViewStats: (params) => crmApi.get('/crm/stats/article-views', { params }),
  getGoogleAnalyticsStats: () => crmApi.get('/crm/stats/google-analytics'),
  getClients: () => crmApi.get('/crm/users/clients'),
  getAttorneys: () => crmApi.get('/crm/users/attorneys'),
  getNotifications: () => crmApi.get('/crm/notifications'),
  clearNotifications: () => crmApi.post('/crm/notifications/clear'),
  clearRequests: () => crmApi.post('/crm/requests/clear'),
  clearSurveys: () => crmApi.delete('/crm/surveys'),
  getArticles: (status) => crmApi.get('/crm/articles', { params: status ? { status } : {} }),
  getArticleDetail: (id) => crmApi.get(`/crm/articles/${id}`),
  getArticlePreview: (id) => crmApi.get(`/crm/articles/${id}/preview-html`, { responseType: 'text' }),
  approveArticle: (id) => crmApi.post(`/crm/articles/${id}/approve`),
  rejectArticle: (id, reason) => crmApi.post(`/crm/articles/${id}/reject`, { reason }),
  approveEdit: (id) => crmApi.post(`/crm/articles/${id}/approve-edit`),
  rejectEdit: (id, reason) => crmApi.post(`/crm/articles/${id}/reject-edit`, { reason }),
  getRatingSubmissions: (status) => crmApi.get('/crm/rating-submissions', { params: status ? { status } : {} }),
  getRatingSubmissionDetail: (id) => crmApi.get(`/crm/rating-submissions/${id}`),
  markRatingSubmissionReviewed: (id) => crmApi.post(`/crm/rating-submissions/${id}/mark-reviewed`),
  reopenRatingSubmission: (id) => crmApi.post(`/crm/rating-submissions/${id}/reopen`),
};

export const getErrorKey = (err) => {
  const msg = err?.response?.data?.error
    || err?.response?.data?.errors?.[0]?.msg
    || err?.message
    || '';
  return BACKEND_ERROR_MAP[msg] || null;
};

export default api;
